import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminFormSection from '../components/AdminFormSection';
import AdminSubpage from '../components/AdminSubpage';
import ProfileImageField from '../components/ProfileImageField';
import '../components/People.css';
import { closetAPI, uploadAPI } from '../services/api';
import {
  applySuggestedProfileFields,
  suggestFullNameFromNames,
  suggestUsernameFromNames,
} from '../utils/influencer-names';

const emptyForm = () => ({
  email: '',
  password: '',
  username: '',
  full_name: '',
  first_name: '',
  last_name: '',
  phone_number: '',
  instagram_handle: '',
  snapchat_handle: '',
  tiktok_handle: '',
  address: '',
  follower_count: '',
  profile_image_url: '',
  is_verified: true,
  bank_name: '',
  bank_account_holder: '',
  bank_iban: '',
  bank_account_number: '',
  bank_swift_code: '',
});

async function fetchInfluencerRow(influencerId, snapshot) {
  if (snapshot && String(snapshot.id) === String(influencerId)) {
    return snapshot;
  }
  for (let page = 1; page <= 50; page++) {
    const res = await closetAPI.getInfluencers({ page, per_page: 50 });
    const rows = res.data.data || [];
    const hit = rows.find((i) => String(i.id) === String(influencerId));
    if (hit) return hit;
    const pg = res.data.pagination;
    const last = pg?.last_page ?? page;
    if (page >= last) break;
  }
  return null;
}

const ClosetInfluencerFormPage = () => {
  const { influencerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEdit = Boolean(influencerId);

  const [formData, setFormData] = useState(emptyForm());
  const [uploadingImage, setUploadingImage] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const autoFillTouched = useRef({ username: false, fullName: false });

  useEffect(() => {
    if (!isEdit) {
      setFormData(emptyForm());
      autoFillTouched.current = { username: false, fullName: false };
      setLoading(false);
      return undefined;
    }

    autoFillTouched.current = { username: true, fullName: true };

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const row = await fetchInfluencerRow(influencerId, location.state?.influencer);
        if (cancelled) return;
        if (row) {
          setFormData({
            email: row.email || '',
            password: '',
            username: row.username || '',
            full_name: row.full_name || '',
            first_name: row.first_name || '',
            last_name: row.last_name || '',
            phone_number: row.phone_number || '',
            instagram_handle: row.instagram_handle || '',
            snapchat_handle: row.snapchat_handle || '',
            tiktok_handle: row.tiktok_handle || '',
            address: row.address || '',
            follower_count: row.follower_count || '',
            profile_image_url: row.profile_image_url || '',
            is_verified: row.is_verified !== false,
            bank_name: row.bank_name || '',
            bank_account_holder: row.bank_account_holder || '',
            bank_iban: row.bank_iban || '',
            bank_account_number: row.bank_account_number || '',
            bank_swift_code: row.bank_swift_code || '',
          });
        } else {
          toast.error('Closet account not found');
        }
      } catch (error) {
        if (!cancelled) toast.error(error.response?.data?.message || error.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [influencerId, isEdit, location.state?.influencer]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      const imageUrl = response.data.image_url;
      setFormData((prev) => ({ ...prev, profile_image_url: imageUrl }));
      toast.success('Image uploaded');
    } catch (error) {
      let errorMessage = 'Error uploading image: ';
      if (error.response?.data?.errors) {
        errorMessage += Object.values(error.response.data.errors).flat().join(', ');
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else {
        errorMessage += error.message;
      }
      toast.error(errorMessage);
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (isEdit) {
        const updateData = { ...formData };
        if (!updateData.password || updateData.password.trim() === '') {
          delete updateData.password;
        }
        if (updateData.profile_image_url === '') {
          updateData.profile_image_url = null;
        }
        await closetAPI.updateInfluencer(influencerId, updateData);
        toast.success('Closet account updated');
      } else {
        const createData = { ...formData };
        if (createData.profile_image_url === '') {
          createData.profile_image_url = null;
        }
        await closetAPI.createInfluencer(createData);
        toast.success('Closet account created');
      }
      navigate('/closet');
    } catch (error) {
      let errorMessage = isEdit ? 'Error updating account: ' : 'Error creating account: ';
      if (error.response?.data?.errors) {
        errorMessage += Object.values(error.response.data.errors).flat().join(', ');
      } else if (error.response?.data?.message) {
        errorMessage += error.response.data.message;
      } else {
        errorMessage += error.message || 'Unknown error';
      }
      toast.error(errorMessage);
    }
  };

  const handleLegalNameChange = (field, value) => {
    setFormData((prev) => {
      const firstName = field === 'first_name' ? value : prev.first_name;
      const lastName = field === 'last_name' ? value : prev.last_name;

      if (isEdit) {
        return { ...prev, [field]: value };
      }

      return applySuggestedProfileFields(
        { ...prev, [field]: value },
        firstName,
        lastName,
        autoFillTouched.current,
      );
    });
  };

  const title = isEdit ? 'Edit closet account' : 'Add closet account';

  return (
    <AdminSubpage title={title} backTo="/closet">
      {loading ? (
        <div className="loading">Loading…</div>
      ) : (
        <div className="admin-form-page">
          <form className="admin-form-stack" onSubmit={handleSubmit}>
            <AdminFormSection title="Account" hint="Login credentials for the influencer closet.">
              <div className="form-group">
                <label htmlFor="closet-email">Email *</label>
                <input
                  id="closet-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="closet-password">
                  {isEdit ? 'New password' : 'Password *'}
                </label>
                <input
                  id="closet-password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  minLength={isEdit ? undefined : 6}
                  required={!isEdit}
                  placeholder={isEdit ? 'Leave blank to keep current' : 'Minimum 6 characters'}
                />
              </div>
            </AdminFormSection>

            <AdminFormSection title="Profile" hint="Full name is shown publicly. Username is used in closet URLs. Legal name is internal only.">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="closet-first-name">First name (internal) *</label>
                  <input
                    id="closet-first-name"
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => handleLegalNameChange('first_name', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="closet-last-name">Last name (internal)</label>
                  <input
                    id="closet-last-name"
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => handleLegalNameChange('last_name', e.target.value)}
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="closet-full-name">Full name (public) *</label>
                <input
                  id="closet-full-name"
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => {
                    autoFillTouched.current.fullName = true;
                    setFormData({ ...formData, full_name: e.target.value });
                  }}
                  required
                  placeholder={suggestFullNameFromNames(formData.first_name, formData.last_name) || 'Display name'}
                />
                <p className="field-hint">Shown in the app and web. Must be unique. Any letters allowed.</p>
              </div>
              <div className="form-group">
                <label htmlFor="closet-username">Username (URL) *</label>
                <input
                  id="closet-username"
                  type="text"
                  value={formData.username}
                  onChange={(e) => {
                    autoFillTouched.current.username = true;
                    setFormData({ ...formData, username: e.target.value.toLowerCase() });
                  }}
                  pattern="[a-z0-9_]{3,30}"
                  minLength={3}
                  maxLength={30}
                  required
                  placeholder={suggestUsernameFromNames(formData.first_name, formData.last_name) || 'mycloset'}
                />
                <p className="field-hint">
                  Used in closet URLs, e.g. /closet/username. Auto-filled from legal name on create; edit manually if needed.
                </p>
              </div>
              <div className="form-group">
                <label htmlFor="closet-phone">Phone number</label>
                <input
                  id="closet-phone"
                  type="text"
                  value={formData.phone_number}
                  onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                  placeholder="+971…"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="closet-instagram">Instagram</label>
                  <input
                    id="closet-instagram"
                    type="text"
                    value={formData.instagram_handle}
                    onChange={(e) =>
                      setFormData({ ...formData, instagram_handle: e.target.value })
                    }
                    placeholder="@username"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="closet-snapchat">Snapchat</label>
                  <input
                    id="closet-snapchat"
                    type="text"
                    value={formData.snapchat_handle}
                    onChange={(e) =>
                      setFormData({ ...formData, snapchat_handle: e.target.value })
                    }
                    placeholder="@username"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="closet-tiktok">TikTok</label>
                  <input
                    id="closet-tiktok"
                    type="text"
                    value={formData.tiktok_handle}
                    onChange={(e) =>
                      setFormData({ ...formData, tiktok_handle: e.target.value })
                    }
                    placeholder="@username"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="closet-followers">Follower count</label>
                  <input
                    id="closet-followers"
                    type="text"
                    value={formData.follower_count}
                    onChange={(e) =>
                      setFormData({ ...formData, follower_count: e.target.value })
                    }
                    placeholder="e.g. 100K, 1.5M"
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="closet-address">Address</label>
                <textarea
                  id="closet-address"
                  rows={3}
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="City, area, or full mailing address"
                />
              </div>
              {isEdit ? (
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={Boolean(formData.is_verified)}
                      onChange={(e) =>
                        setFormData({ ...formData, is_verified: e.target.checked })
                      }
                    />
                    {' '}
                    Approved — visible in the app
                  </label>
                </div>
              ) : null}
              <ProfileImageField
                value={formData.profile_image_url}
                onChange={(url) => setFormData({ ...formData, profile_image_url: url })}
                onUpload={handleImageUpload}
                uploading={uploadingImage}
                person={formData}
              />
            </AdminFormSection>

            <AdminFormSection
              title="Bank details"
              hint="Used for closet payout transfers. Not shown in the sidebar — edit here per account."
            >
              <div className="form-group">
                <label htmlFor="closet-bank-name">Bank name</label>
                <input
                  id="closet-bank-name"
                  type="text"
                  value={formData.bank_name}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label htmlFor="closet-account-holder">Account holder name</label>
                <input
                  id="closet-account-holder"
                  type="text"
                  value={formData.bank_account_holder}
                  onChange={(e) =>
                    setFormData({ ...formData, bank_account_holder: e.target.value })
                  }
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="closet-iban">IBAN</label>
                  <input
                    id="closet-iban"
                    type="text"
                    value={formData.bank_iban}
                    onChange={(e) => setFormData({ ...formData, bank_iban: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="closet-account-number">Account number</label>
                  <input
                    id="closet-account-number"
                    type="text"
                    value={formData.bank_account_number}
                    onChange={(e) =>
                      setFormData({ ...formData, bank_account_number: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="closet-swift">SWIFT / BIC</label>
                <input
                  id="closet-swift"
                  type="text"
                  value={formData.bank_swift_code}
                  onChange={(e) => setFormData({ ...formData, bank_swift_code: e.target.value })}
                />
              </div>
            </AdminFormSection>

            <div className="admin-form-footer">
              <button type="submit" className="btn btn-primary" disabled={uploadingImage}>
                {isEdit ? 'Save changes' : 'Create account'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => navigate('/closet')}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminSubpage>
  );
};

export default ClosetInfluencerFormPage;
